import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError, useApi, type EpisodeDetail, type Rec } from '../api'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { usePlayer } from '../player'
import { fmtClock, fmtDateLong, fmtDuration, fmtPct, kindOf } from '../format'
import { AiNote } from '../components/Disclosures'
import { Conv, DirPill, Empty, ErrorState, Icon, Loading, NotFound, PodAv } from '../components/primitives'

type Episode = EpisodeDetail['episode']

function useEpisodePlayer(ep: Episode | undefined) {
  const player = usePlayer()
  const loaded = !!ep && player.src === ep.audio_url
  return {
    playing: player.key === `ep:${ep?.id}` && player.playing,
    // Any clip from this episode moves the scrubber.
    time: loaded ? player.time : 0,
    error: loaded ? player.error : null,
    playPause: () => ep && player.play({ key: `ep:${ep.id}`, src: ep.audio_url, start: 0, end: null }),
    seek: (t: number) => ep && player.play({ key: `ep:${ep.id}`, src: ep.audio_url, start: t, end: null, restart: true }),
    clip: (r: Rec) => ep && player.play({ key: `rec:${r.id}`, src: ep.audio_url, start: r.timestamp_seconds, end: r.clip_end_seconds }),
    clipPlaying: (r: Rec) => player.key === `rec:${r.id}` && player.playing,
  }
}

function Transcript({ id, onSeek, mobile }: { id: string; onSeek: (t: number) => void; mobile: boolean }) {
  const { data, error, retry } = useApi<{ start: number; end: number; text: string }[]>(api.transcript(id))
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return <Loading label="Loading transcript…" />
  return (
    <div className={mobile ? 'm-card' : 'card'} style={{ marginTop: 12, maxHeight: 420, overflowY: 'auto' }} data-testid="transcript">
      {data.map((s, i) => (
        <p key={i} style={{ margin: '0 0 10px', fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)' }}>
          <button className="ts-link" onClick={() => onSeek(s.start)}>{fmtClock(s.start)}</button> {s.text}
        </p>
      ))}
    </div>
  )
}

function counts(recs: Rec[]) {
  return {
    buy: recs.filter((r) => r.type === 'buy').length,
    hold: recs.filter((r) => r.type === 'hold').length,
    sell: recs.filter((r) => r.type === 'sell').length,
  }
}

function SinceCell({ r, size }: { r: Rec; size?: number }) {
  if (r.pick_return_pct == null) return null
  return (
    <span className={`t-num ${r.pick_return_pct >= 0 ? 'text-up' : 'text-down'}`} style={size ? { fontSize: size } : undefined}>
      {fmtPct(r.pick_return_pct)} since
    </span>
  )
}

export function Episode() {
  const { id = '' } = useParams()
  const mobile = useIsMobile()
  const { data, error, retry } = useApi<EpisodeDetail>(api.episode(id))
  usePageTitle(data?.episode.title)
  const audio = useEpisodePlayer(data?.episode)
  const [showTranscript, setShowTranscript] = useState(false)

  if (error instanceof ApiError && error.status === 404) {
    return <NotFound what="Episode"><Link to="/episodes">All episodes</Link></NotFound>
  }
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return <Loading />

  const { episode: ep, recommendations: recs } = data
  const duration = ep.duration_seconds ?? 0
  const at = (t: number) => (duration > 0 ? Math.min(100, (t / duration) * 100) : 0)
  const c = counts(recs)
  const meta = `${fmtDateLong(ep.pub_date)}${ep.duration_seconds ? ` · ${fmtDuration(ep.duration_seconds)}` : ''}`

  const markers = (cls: string) =>
    recs.map((r) => (
      <button
        key={r.id}
        className={`${cls} ${kindOf(r.type) === 'up' ? '' : kindOf(r.type)}`}
        style={{ left: `${at(r.timestamp_seconds)}%` }}
        title={`${r.ticker} · ${r.type?.toUpperCase()} @ ${r.timestamp_label}`}
        aria-label={`Play from ${r.ticker} at ${r.timestamp_label}`}
        onClick={() => audio.seek(r.timestamp_seconds)}
        data-testid="scrub-marker"
      />
    ))

  if (mobile) {
    return (
      <div data-testid="episode-page">
        <Link to={`/podcasts/${ep.podcast_id}`} className="row ai-c g-10" style={{ marginTop: 4, color: 'inherit', textDecoration: 'none' }}>
          <PodAv name={ep.podcast_name} image={ep.podcast_image_url} size="lg sq" />
          <div className="col" style={{ minWidth: 0 }}>
            <span style={{ fontSize: 14.5, fontWeight: 500 }}>{ep.podcast_name}</span>
            <span className="t-sm" style={{ color: 'var(--ink-3)' }}>{fmtDateLong(ep.pub_date)}</span>
          </div>
        </Link>
        <h1 className="m-h1" style={{ marginTop: 14 }}>{ep.title}</h1>
        <p className="m-sub" style={{ marginBottom: 14 }}>{meta}</p>

        <div className="m-scrub">
          <button className="play" onClick={audio.playPause} aria-label={audio.playing ? 'Pause' : 'Play episode'} data-testid="play-episode">
            <Icon name={audio.playing ? 'pause' : 'play'} size={16} />
          </button>
          <div className="col grow g-6">
            <div className="track">
              <div className="fill" style={{ width: `${at(audio.time)}%` }} />
              {markers('mk')}
            </div>
            <div className="row jc-sb">
              <span className="time">{fmtClock(audio.time)}</span>
              <span className="time">{duration ? fmtClock(duration) : ''}</span>
            </div>
          </div>
        </div>
        {audio.error && <p className="note warn" style={{ marginTop: 8 }}>{audio.error}</p>}
        <button className="m-btn" style={{ marginTop: 10 }} onClick={() => setShowTranscript((v) => !v)}>
          {showTranscript ? 'Hide transcript' : 'Transcript'}
        </button>
        {showTranscript && <Transcript id={ep.id} onSeek={audio.seek} mobile />}

        <div className="m-sec">
          <h3>Recommendations <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>({recs.length})</span></h3>
        </div>
        {recs.length > 0 && <AiNote />}
        {recs.length === 0 ? (
          <Empty>No calls in this episode.</Empty>
        ) : (
          <div className="m-feed">
            {recs.map((r) => (
              <div key={r.id} className="m-card m-rec">
                <Link to={`/rec/${r.id}`} className="hd" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="tk"><span className="tick" style={{ fontSize: 16 }}>{r.ticker}</span><DirPill type={r.type} /></div>
                    <div className="co">{r.company_name}</div>
                  </div>
                  <div className="mv"><span className="big"><SinceCell r={r} size={16} /></span></div>
                </Link>
                <Link to={`/rec/${r.id}`} className="m-epquote" style={{ textDecoration: 'none' }}>{r.thesis}</Link>
                <div className="ft">
                  <button className="ts-btn" onClick={() => audio.clip(r)} aria-label={`Play clip at ${r.timestamp_label}`}>
                    <Icon name={audio.clipPlaying(r) ? 'pause' : 'play'} size={12} /> {r.timestamp_label}
                  </button>
                  <Conv n={r.conviction} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div data-testid="episode-page">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <Link to="/">Home</Link>
        <span className="sep" aria-hidden>/</span>
        <Link to={`/podcasts/${ep.podcast_id}`}>{ep.podcast_name}</Link>
        <span className="sep" aria-hidden>/</span>
        <span className="now" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>{ep.title}</span>
      </div>

      <div className="row ai-fs g-16" style={{ marginBottom: 22 }}>
        <PodAv name={ep.podcast_name} image={ep.podcast_image_url} size="xl" />
        <div className="col g-4 grow">
          <div className="t-xs">{ep.podcast_name} · {fmtDateLong(ep.pub_date)}</div>
          <h1 className="page-title" style={{ fontSize: 30, marginBottom: 2 }}>{ep.title}</h1>
          <div className="t-sm">
            <Link to={`/podcasts/${ep.podcast_id}`} style={{ color: 'var(--ink)' }}>{ep.podcast_name}</Link> · {meta}
          </div>
        </div>
        <div className="row g-8">
          <button className="btn" onClick={() => setShowTranscript((v) => !v)}>{showTranscript ? 'Hide transcript' : 'Transcript'}</button>
          <button className="btn primary" onClick={audio.playPause} data-testid="play-episode">
            <Icon name={audio.playing ? 'pause' : 'play'} size={14} /> {audio.playing ? 'Pause' : 'Play'}
          </button>
        </div>
      </div>

      <div className="scrubber">
        <button className="play" onClick={audio.playPause} aria-label={audio.playing ? 'Pause' : 'Play episode'}>
          <Icon name={audio.playing ? 'pause' : 'play'} size={12} />
        </button>
        <span className="time">{fmtClock(audio.time)}</span>
        <div className="track">
          <div className="fill" style={{ width: `${at(audio.time)}%` }} />
          {markers('marker')}
        </div>
        <span className="time">{duration ? fmtClock(duration) : ''}</span>
      </div>
      {audio.error && <p className="note warn" style={{ marginTop: 8 }}>{audio.error}</p>}
      {showTranscript && <Transcript id={ep.id} onSeek={audio.seek} mobile={false} />}

      <div className="mt-24">
        <div className="row ai-c jc-sb mb-12">
          <h3 className="serif" style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>
            Recommendations extracted <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>({recs.length})</span>
          </h3>
          <div className="row g-6">
            <span className="pill accent">{c.buy} BUY</span>
            <span className="pill hold">{c.hold} HOLD</span>
            <span className="pill down">{c.sell} SELL</span>
          </div>
        </div>
        {recs.length > 0 && <AiNote />}
        {recs.length === 0 ? (
          <Empty>No calls in this episode.</Empty>
        ) : (
          <div className="col g-10">
            {recs.map((r) => (
              <div key={r.id} className="ep-rec">
                <span className="stamp">{r.timestamp_label}</span>
                <Link to={`/rec/${r.id}`} className="body" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <div className="top">
                    <span className="tick" style={{ fontSize: 15 }}>{r.ticker}</span>
                    <span className="t-sm">{r.company_name}</span>
                    <DirPill type={r.type} />
                    <Conv n={r.conviction} />
                    <span className="grow" />
                    <SinceCell r={r} />
                  </div>
                  <div className="quote">{r.thesis}</div>
                </Link>
                <button className="btn ghost" style={{ padding: '6px 8px' }} onClick={() => audio.clip(r)} aria-label={`Play clip at ${r.timestamp_label}`}>
                  <Icon name={audio.clipPlaying(r) ? 'pause' : 'play'} size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
